import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * Memory Monitoring Service
 * 
 * Tracks memory usage in development mode to detect leaks early
 */
@Injectable({
  providedIn: 'root'
})
export class MemoryMonitorService {
  private measurements: number[] = [];
  private intervalId: any;

  constructor() {
    if (!environment.production && environment.debug) {
      this.startMonitoring();
    }
  }

  startMonitoring(): void {
    if (!(performance as any).memory) {
      console.warn('Performance.memory API not available');
      return;
    }

    this.intervalId = setInterval(() => {
      const memory = (performance as any).memory;
      const usedMB = memory.usedJSHeapSize / 1048576;
      
      this.measurements.push(usedMB);
      
      // Keep only last 100 measurements
      if (this.measurements.length > 100) {
        this.measurements.shift();
      }

      // Detect continuous growth (potential leak)
      if (this.measurements.length >= 20) {
        const recent = this.measurements.slice(-20);
        const average = recent.reduce((a, b) => a + b) / recent.length;
        const trend = recent[recent.length - 1] - recent[0];
        
        if (trend > 20) { // Growing by 20MB over 20 measurements
          console.warn('⚠️ Potential memory leak detected!');
          console.warn(`Memory trend: +${trend.toFixed(2)}MB`);
          console.warn(`Current usage: ${usedMB.toFixed(2)}MB`);
          console.warn('Check for unsubscribed observables');
        }
      }

      // Log every 30 seconds in debug mode
      if (this.measurements.length % 30 === 0) {
        console.log(`📊 Memory: ${usedMB.toFixed(2)}MB`);
      }
    }, 1000);
  }

  stopMonitoring(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  getReport(): { current: number; average: number; trend: number } {
    if (!(performance as any).memory) {
      return { current: 0, average: 0, trend: 0 };
    }

    const memory = (performance as any).memory;
    const current = memory.usedJSHeapSize / 1048576;
    const average = this.measurements.reduce((a, b) => a + b, 0) / this.measurements.length;
    const trend = this.measurements.length >= 2 
      ? this.measurements[this.measurements.length - 1] - this.measurements[0]
      : 0;

    return { current, average, trend };
  }
}